import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { NoteFormValues } from 'modules/notes/notePopup/noteEdit/utils';
import { notifyUserError } from 'utils/notifications';

import {
  getCurrentAIScribe,
  initializeRecordingSession,
  startRecordingTranscription,
  uploadRecordingChunk
} from './aiScribeActions';

export const noteTypeData = [
  { id: 'CHART_REVIEW', name: 'Chart Audit' },
  { id: 'END_OF_LIFE', name: 'End of Life' },
  { id: 'ENROLLMENT', name: 'Enrollment' },
  { id: 'ENROLLMENT_CHILD', name: 'Enrollment - Child' },
  { id: 'ENROLLMENT_TEEN', name: 'Enrollment - Teen' },
  { id: 'FOLLOW_UP', name: 'Follow-up' },
  { id: 'GRADUATION', name: 'Graduation' },
  { id: 'HEALTH_GOAL', name: 'Health Goal' },
  { id: 'HOSPITALIZATION', name: 'Hospitalization' },
  { id: 'MEET_THE_PERSON_INTERVIEW', name: 'Meet the Person Interview' },
  { id: 'MEET_THE_PERSON_INTERVIEW_CHILD', name: 'Meet the Person Interview - Child' },
  { id: 'MEET_THE_PERSON_INTERVIEW_TEEN', name: 'Meet the Person Interview - Teen' },
  { id: 'MID_PREGNANCY_FOLLOW_UP', name: 'Mid-Pregnancy Follow-up' },
  { id: 'POST_DELIVERY', name: 'Post-Delivery' },
  { id: 'PROVIDER_APPOINTMENT', name: 'Provider Appointment' },
  { id: 'SAFETY_NOTE', name: 'Safety Note' }
];

export type AiScribeStatus =
  | 'idle'
  | 'initializing'
  | 'recording'
  | 'paused'
  | 'interrupted'
  | 'reacquiring_microphone'
  | 'finishing'
  | 'transcribing'
  | 'transcribed'
  | 'failed';

export const aiScribeRecordingStatuses: AiScribeStatus[] = [
  'recording',
  'paused',
  'interrupted',
  'reacquiring_microphone',
  'finishing'
];

export const isAiScribeRecordingStatus = (status: AiScribeStatus) =>
  aiScribeRecordingStatuses.includes(status);

interface AiScribeSliceProps {
  state: 'expanded' | 'collapsed' | null;
  status: AiScribeStatus;
  isPaused: boolean;
  error: string | null;
  clientId: number | null;
  clientName: string | null;
  noteType?: string | null;
  fileUuid: string | null;
  lastOrdinalNumber: number | null;
  prependedClientId: number | string | null;
  duration: number;
  isResumedFromExternalSource: boolean;
  note?: NoteFormValues | null;
  initializing: boolean;
  uploadingChunk: boolean;
  transcribing: boolean;
}

const initialState: AiScribeSliceProps = {
  state: null,
  status: 'idle',
  error: null,
  isPaused: false,
  clientId: null,
  clientName: null,
  noteType: null,
  fileUuid: null,
  lastOrdinalNumber: null,
  prependedClientId: null,
  duration: 0,
  isResumedFromExternalSource: false,
  note: null,
  initializing: false,
  uploadingChunk: false,
  transcribing: false
};

const aiScribeSlice = createSlice({
  name: 'aiScribeSlice',
  initialState,
  reducers: {
    openAiScribeDrawer: (
      state,
      action: PayloadAction<{
        clientId: number;
        clientName: string;
        prependedClientId: number | string;
        note?: NoteFormValues;
      }>
    ) => {
      state.state = 'expanded';
      state.clientId = action.payload.clientId;
      state.prependedClientId = action.payload.prependedClientId;
      state.clientName = action.payload.clientName;
      state.note = action.payload.note;
    },
    closeAiScribeDrawer: () => ({ ...initialState }),
    minimizeAiScribeDrawer: (state) => {
      state.state = 'collapsed';
    },
    expandAiScribeDrawer: (state) => {
      state.state = 'expanded';
    },
    setStatus: (state, action: PayloadAction<AiScribeSliceProps['status']>) => {
      state.status = action.payload;
    },
    pauseAiScribe: (state) => {
      state.isPaused = true;
    },
    resumeAiScribe: (state) => {
      state.isPaused = false;
    },
    setDuration: (state, action: PayloadAction<number>) => {
      state.duration = action.payload;
    },
    resetDuration: (state) => {
      state.duration = 0;
    },
    setNoteType: (state, action: PayloadAction<string>) => {
      state.noteType = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(initializeRecordingSession.pending, (state) => {
        state.initializing = true;
        state.error = null;
      })
      .addCase(initializeRecordingSession.fulfilled, (state) => {
        state.initializing = false;
        state.error = null;
      })
      .addCase(initializeRecordingSession.rejected, (state) => {
        state.initializing = false;
        state.error = 'System error. Please try again';
      })
      .addCase(uploadRecordingChunk.pending, (state) => {
        state.uploadingChunk = true;
      })
      .addCase(uploadRecordingChunk.fulfilled, (state, { payload }: any) => {
        state.uploadingChunk = false;
        if (payload.status === 'ERROR') {
          state.error = 'System error: Consider taking notes.';
        } else if (payload.status === 'SUCCESS') {
          state.error = null;
        }
      })
      .addCase(uploadRecordingChunk.rejected, (state) => {
        state.uploadingChunk = false;
        state.error = 'System error: Consider taking notes.';
      })
      .addCase(startRecordingTranscription.pending, (state) => {
        state.transcribing = true;
      })
      .addCase(startRecordingTranscription.fulfilled, (state) => {
        state.transcribing = false;
      })
      .addCase(startRecordingTranscription.rejected, (state, { payload }: any) => {
        state.transcribing = false;
        state.error = payload;
      })
      .addCase(getCurrentAIScribe.fulfilled, (state, { payload }) => {
        if (!payload) {
          return;
        }

        state.state = 'expanded';
        state.status = 'recording';
        state.isPaused = true;
        state.error = null;
        state.clientId = Number(payload.clientId);
        state.clientName = payload.clientName;
        state.fileUuid = payload.fileUuid;
        state.lastOrdinalNumber = Number(payload.lastOrdinalNumber);
        state.prependedClientId = payload.prependedClientId;
        state.duration = payload.duration;
        state.isResumedFromExternalSource = true;
      })
      .addCase(getCurrentAIScribe.rejected, (_, { payload }: any) => {
        notifyUserError(payload);
      });
  }
});

export const {
  openAiScribeDrawer,
  closeAiScribeDrawer,
  minimizeAiScribeDrawer,
  expandAiScribeDrawer,
  setStatus,
  pauseAiScribe,
  resumeAiScribe,
  setDuration,
  resetDuration,
  setNoteType,
  setError
} = aiScribeSlice.actions;

export default aiScribeSlice.reducer;
